import type Lenis from 'lenis';

/**
 * Module-level registry for the app's single Lenis instance.
 *
 * Everything that needs to scroll the page programmatically goes through the
 * helpers here rather than calling `window.scrollTo` directly. Two reasons:
 *
 * 1. Lenis owns the document's scroll position while it is active. A native
 *    `window.scrollTo({behavior: 'smooth'})` runs the browser's own animation
 *    against the same scrollTop that Lenis is writing every frame — the two
 *    fight and the scroll judders or snaps.
 * 2. Lenis only mounts on desktop, fine-pointer, non-reduced-motion visitors,
 *    so every helper must degrade to the native behaviour when it is absent.
 *
 * The `import type` above is load-bearing: it is erased at compile time, so
 * this module adds nothing to the bundles of the routes that never pull in
 * Lenis's async chunk (SmoothScrollProvider).
 */

let instance: Lenis | null = null;

export const registerLenis = (next: Lenis | null): void => {
    instance = next;
};

export const getLenis = (): Lenis | null => instance;

/** Smooth-scrolls to an absolute document offset. */
export const scrollToY = (top: number): void => {
    if (instance) {
        instance.scrollTo(top);
        return;
    }
    globalThis.scrollTo({top, behavior: 'smooth'});
};

/**
 * Smooth-scrolls an element into view, leaving `offset` pixels of clearance for
 * the sticky nav. Callers pass their own offset — the site's header height
 * differs per template, so these values are deliberately NOT unified.
 */
export const scrollToElement = (element: Element, offset = 0): void => {
    // Sections with `content-visibility: auto` (CvSection) that have not been on screen yet still count at
    // their estimated height, so a target below them would be measured short. Render them once so the
    // position is exact; `contain-intrinsic-size: auto` then remembers their real heights when they are
    // switched back on the next frame.
    const skipped = document.querySelectorAll<HTMLElement>('[style*="content-visibility"]');
    skipped.forEach((el) => el.style.setProperty('content-visibility', 'visible'));
    requestAnimationFrame(() => skipped.forEach((el) => el.style.setProperty('content-visibility', 'auto')));

    if (instance) {
        instance.scrollTo(element as HTMLElement, {offset: -offset});
        return;
    }
    globalThis.scrollTo({
        top: element.getBoundingClientRect().top + globalThis.scrollY - offset,
        behavior: 'smooth'
    });
};

export const scrollToTop = (): void => scrollToY(0);

/**
 * Suspends/resumes Lenis. Needed whenever something else takes ownership of
 * page scrolling — MUI's Modal and Drawer set `overflow: hidden` on <body>,
 * which Lenis neither knows about nor respects.
 */
export const pauseSmoothScroll = (): void => instance?.stop();
export const resumeSmoothScroll = (): void => instance?.start();

/**
 * Binds GSAP ScrollTrigger to the Lenis frame loop. Call from the page types
 * that load GSAP (homepage `HomeWhyChoose`, case-study `Solution`) after their
 * dynamic GSAP import resolves. Returns an unbind function, or a no-op when
 * Lenis is not active.
 *
 * Without this, ScrollTrigger updates from the browser's native `scroll` event,
 * which lands a frame after Lenis has already moved the content — visible as
 * scrub animations and pins trailing the page by ~16ms.
 */
export const bindScrollTrigger = (scrollTrigger: {update: () => void}): (() => void) => {
    const lenis = instance;
    if (!lenis) return () => undefined;

    const update = () => scrollTrigger.update();
    lenis.on('scroll', update);
    return () => lenis.off('scroll', update);
};
